import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, Modal } from 'antd';
import {
  ClockCircleOutlined,
  LoadingOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import AutoThresholdConfigModal from './AutoThresholdConfigModal';
import style from './index.scss';

// FIN-12229 — epoch millis -> YYYY-MM-DD (matches the config dialog's RangePicker display).
const formatDay = (epochMs) => {
  if (epochMs == null) return null;
  const m = moment(Number(epochMs));
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
};

const ThresholdGenerationBanner = ({
  generationState,
  startedAt,
  isTriggering,
  scenarioId,
  onTrigger,
  onDiscard,
  // FIN-12229 — generation completion time (auto_threshold_result.created_at, forwarded as
  // AutoThresholdResponse.createdAt) and the dataset date range (auto-threshold config
  // execStartDate/execEndDate, epoch millis) used for this generation.
  generatedAt = null,
  dataStartDate = null,
  dataEndDate = null
}) => {
  const [modalVisible, setModalVisible] = useState(false);

  const handleTriggerConfirm = (config) => {
    onTrigger({ scenarioId, config });
    setModalVisible(false);
  };

  const handleDiscard = () => {
    Modal.confirm({
      title: 'Discard Thresholds?',
      content:
        "This will clear all threshold suggestions and unlock the scenario for editing. You'll need to run the full pipeline again.",
      okText: 'Discard',
      okType: 'danger',
      onOk: (close) => {
        onDiscard({ scenarioId });
        close();
      }
    });
  };

  const renderConfiguring = () => (
    <div
      className={`${style.thresholdBanner} ${style['thresholdBanner--configuring']}`}>
      <ClockCircleOutlined
        className={`${style.thresholdBanner__icon} ${style['thresholdBanner__icon--configuring']}`}
      />
      <div className={style.thresholdBanner__content}>
        <div className={style.thresholdBanner__title}>Threshold Generation</div>
        <p className={style.thresholdBanner__text}>
          Configure all variants, then generate thresholds.
        </p>
      </div>
      <div className={style.thresholdBanner__action}>
        <Button
          type="primary"
          loading={isTriggering}
          onClick={() => setModalVisible(true)}>
          Generate Thresholds
        </Button>
      </div>
    </div>
  );

  const renderGenerating = () => {
    const formattedStart = startedAt
      ? new Date(startedAt).toLocaleString()
      : null;

    return (
      <div
        className={`${style.thresholdBanner} ${style['thresholdBanner--generating']}`}>
        <LoadingOutlined
          className={`${style.thresholdBanner__icon} ${style['thresholdBanner__icon--generating']}`}
        />
        <div className={style.thresholdBanner__content}>
          <div className={style.thresholdBanner__title}>
            Threshold Generation
          </div>
          <p className={style.thresholdBanner__text}>
            Extracting features from dataset...
          </p>
          {formattedStart && (
            <p className={style.thresholdBanner__subtext}>
              Started at {formattedStart}
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderGenerated = () => {
    // FIN-12229 — show WHEN the thresholds were generated and WHICH dataset window produced them.
    const generatedAtText = generatedAt
      ? moment(generatedAt).isValid()
        ? moment(generatedAt).format('YYYY-MM-DD HH:mm')
        : generatedAt
      : null;
    const from = formatDay(dataStartDate);
    const to = formatDay(dataEndDate);
    const dataRangeText = from && to ? `${from} → ${to}` : null;

    return (
      <div
        className={`${style.thresholdBanner} ${style['thresholdBanner--generated']}`}>
        <CheckCircleOutlined
          className={`${style.thresholdBanner__icon} ${style['thresholdBanner__icon--generated']}`}
        />
        <div className={style.thresholdBanner__content}>
          <div className={style.thresholdBanner__title}>
            Thresholds generated.
          </div>
          {(generatedAtText || dataRangeText) && (
            <p className={style.thresholdBanner__subtext}>
              {generatedAtText && <>Generated at {generatedAtText}</>}
              {generatedAtText && dataRangeText && ' · '}
              {dataRangeText && <>data range {dataRangeText}</>}
            </p>
          )}
        </div>
        <div className={style.thresholdBanner__action}>
          <Button onClick={handleDiscard}>Discard Thresholds</Button>
        </div>
      </div>
    );
  };

  const renderers = {
    configuring: renderConfiguring,
    generating: renderGenerating,
    generated: renderGenerated
  };

  const renderBanner = renderers[generationState] || renderConfiguring;

  return (
    <>
      {renderBanner()}
      <AutoThresholdConfigModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onConfirm={handleTriggerConfirm}
        isLoading={isTriggering}
      />
    </>
  );
};

ThresholdGenerationBanner.propTypes = {
  generationState: PropTypes.oneOf(['configuring', 'generating', 'generated']),
  startedAt: PropTypes.string,
  isTriggering: PropTypes.bool,
  scenarioId: PropTypes.number,
  onTrigger: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
  generatedAt: PropTypes.string,
  dataStartDate: PropTypes.number,
  dataEndDate: PropTypes.number
};

ThresholdGenerationBanner.defaultProps = {
  generationState: 'configuring',
  startedAt: null,
  isTriggering: false,
  scenarioId: null
};

export default ThresholdGenerationBanner;
